import React, { useState, useCallback, forwardRef } from 'react';
import { getSims } from '../../../services/sims/simsService';

const AutocompleteSimcard = forwardRef(({ 
    id,
    name,
    value,
    onChange,
    placeholder,
    disabled = false,
    maxSuggestions = 100
}, ref) => { 
    const [simcards, setSimcards] = useState([]);
    const [filteredSimcards, setFilteredSimcards] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);

    const loadSimcards = useCallback(async (searchText) => {
        if (!searchText?.trim() || searchText.length < 3) {
          setFilteredSimcards([]);
          return;
        }
    
        try {
            setLoading(true);
            const response = await getSims(1, maxSuggestions);
            setSimcards(response.content);
            setHasFetched(true);
            
            const filtered = response.content
                .filter(simcard => simcard.iccid.toLowerCase().includes(searchText.toLowerCase()))
                .slice(0, maxSuggestions);
            
            setFilteredSimcards(filtered);
        } catch (error) {
            console.error('Error al cargar las simcards:', error);
            setFilteredSimcards([]);
        } finally {
            setLoading(false);
        }
    }, [maxSuggestions]);
    
    const handleChange = async (event) => {
        const simcardInput = event.target.value;
        onChange(event); // Asegúrate de que el padre maneje el cambio
    
        if (searchTimeout)
            clearTimeout(searchTimeout);
    
        if (simcardInput.length >= 3) {
            setLoading(true);
    
            const timeout = setTimeout(async () => {
                if (!hasFetched) {
                    await loadSimcards(simcardInput);
                } else {
                    const filtered = simcards
                        .filter(simcard => 
                            simcard.iccid.toLowerCase().includes(simcardInput.toLowerCase()))
                        .slice(0, maxSuggestions);
    
                    setFilteredSimcards(filtered);
                    setLoading(false);
                }
                setShowSuggestions(true);
            }, 300); // Debounce de 300ms
    
            setSearchTimeout(timeout);
        } else {
            setLoading(false);
            setFilteredSimcards([]);
            setShowSuggestions(false);
        }
    };
    
    const handleClick = (simcard) => {
        onChange({ target: { name, value: simcard } }); // Envía el objeto completo
        setFilteredSimcards([]);
        setShowSuggestions(false);
    };
    
    const handleKeyDown = (event) => {
        if (event.key === 'Escape')
          setShowSuggestions(false);
    };
    
    const handleBlur = () => {
        setTimeout(() => {
            setShowSuggestions(false);
        }, 200);
    };
    
    const SuggestionsListComponent = () => {
        if (!showSuggestions || !value || value.length < 3) 
            return null;
    
        return (
            <ul role="listbox"
                className="absolute pl-0 w-full z-10 mt-1 max-h-60 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg"
            >
                {loading ? (
                    <li className="p-2 text-sm text-blue-600 italic">
                        Cargando...
                    </li>
                ) : filteredSimcards.length > 0 ? (
                    filteredSimcards.map((simcard) => (
                        <li key={simcard.id}  onClick={() => handleClick(simcard)} // Pasa el objeto completo
                            className="p-2 text-sm text-gray-700 cursor-pointer hover:bg-blue-50 focus:bg-blue-50"
                        >
                            {simcard.iccid}
                        </li>
                    ))
                ) : (
                    <li className="p-2 text-sm text-blue-600 italic">
                        No hay sugerencias disponibles.
                    </li>
                )}
            </ul>
        );
    };

    return(
        <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className={`material-icons ${loading ? 'animate-spin' : ''} text-blue-600`}>
                    {loading ? 'autorenew' : 'search'}
                </span>
            </div>
            
            <input
                id={id || name}
                autoComplete='off'
                type="text"
                name={name}
                value={value ? value.iccid : ''} // Muestra el iccid si el valor es un objeto
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                aria-autocomplete="list"
                className='block w-full pl-10 pr-3 py-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md'
            />
            <SuggestionsListComponent />
        </div>
    );
});

// Opcional pero recomendado: añadir un displayName
AutocompleteSimcard.displayName = 'AutocompleteSimcard';

export default AutocompleteSimcard;